import React, { forwardRef, useMemo } from 'react';
import { Checkbox, Skeleton, Tooltip } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { useNavigate } from 'react-router-dom';
import { FoldersTable } from '@components/Tables';
import { AssetItemDto } from '@api/Api';
import { reorganizeFolderList } from '@redux/actions/assetsAction';
import { DraggableList } from '@components/DraggableList/DraggableList';
import { DraggableItem } from '@components/DraggableList';
import useTypedDispatch from '@hooks/useTypedDispatch';
import FolderActions from '@components/Tables/FolderActions';
import { FolderIcon, FolderPrivateIcon } from '@icons';
import { showReorderRequiresCustomSortHint } from '@helpers/showReorderRequiresCustomSortHint';

function FoldersList({
  foldersList,
  countToFetch,
  isCardViewMode,
  canReorganize,
  canDropInside,
  listLoading,
  onAssetDrop,
  onFolderDrop,
  canShare,
  selectedFolders = [],
  setSelectedFolders,
  foldersCount = 0,
  showSelectAll = true
}: {
  foldersList: {
    node: AssetItemDto;
    cursor: string;
  }[];
  countToFetch: number;
  isCardViewMode: boolean;
  canReorganize?: boolean;
  canDropInside?: boolean;
  listLoading: boolean;
  onAssetDrop?: (assetId: string, folderId: string) => void;
  onFolderDrop?: (sourceFolderId: string, targetFolderId: string) => void;
  canShare?: boolean;
  selectedFolders?: {
    node: AssetItemDto;
    cursor: string;
  }[];
  setSelectedFolders?: (
    ids: string[],
    folders: { node: AssetItemDto; cursor: string }[],
    isAllSelect: boolean
  ) => void;
  foldersCount?: number;
  showSelectAll?: boolean;
}) {
  const dispatch = useTypedDispatch();

  const onCheckboxChange = (v: CheckboxValueType[]) => {
    if (!setSelectedFolders) return;
    const folders = v
      .map((el) => {
        const folder = foldersList.find((folder) => folder.node.id === el);
        if (folder) return folder;
        return { node: null, cursor: '' };
      })
      .filter((item) => !!item.node) as {
      node: AssetItemDto;
      cursor: string;
    }[];

    setSelectedFolders(
      folders.map((x) => x.node.id),
      folders,
      false
    );
  };

  if (!foldersList.length && !countToFetch) return null;

  return (
    <div>
      {isCardViewMode ? (
        <Checkbox.Group
          value={selectedFolders.map((el) => el?.node?.id || '')}
          style={{
            display: 'block',
            paddingRight: 34,
            paddingLeft: 34,
            marginRight: -34,
            marginLeft: -34
          }}
          onChange={onCheckboxChange}
        >
          <div className="assets-container">
            <DraggableList
              className="assets-container-list"
              gap={32}
              onReorder={(e) => {
                if (!canReorganize) {
                  showReorderRequiresCustomSortHint();
                  return;
                }
                dispatch(reorganizeFolderList(e));
              }}
              onDropInside={(e) => {
                if (e.item.type === 'asset' && onAssetDrop) {
                  onAssetDrop(e.item.id, e.insideItem.id);
                }
                if (e.item.type === 'folder' && onFolderDrop) {
                  onFolderDrop(e.item.id, e.insideItem.id);
                }
              }}
            >
              {foldersList
                .concat(new Array(countToFetch).fill(''))
                .map(({ node }, index) => {
                  if (!node)
                    return (
                      <div key={index} className="assets_folder_container">
                        <Skeleton.Input style={{ width: '100%', height: 56 }} />
                      </div>
                    );

                  return (
                    <DraggableItem
                      key={node.id}
                      id={node.id}
                      type="folder"
                      droppable={canDropInside ? ['asset', 'folder'] : false}
                    >
                      <FolderEntity
                        node={node}
                        canShare={canShare}
                        showCheckbox={!!setSelectedFolders}
                      />
                    </DraggableItem>
                  );
                })}
            </DraggableList>
          </div>
        </Checkbox.Group>
      ) : (
        <div
          style={{
            display: 'block',
            paddingRight: 34,
            paddingLeft: 34,
            marginRight: -34,
            marginLeft: -34
          }}
        >
          <div className="assets-container-list">
            <FoldersTable
              foldersList={foldersList}
              onDrop={onAssetDrop}
              onFolderDrop={onFolderDrop}
              onReorder={(e) => dispatch(reorganizeFolderList(e))}
              canReorganize={canReorganize}
              countToFetch={countToFetch}
              listLoading={listLoading}
              canShare={canShare}
              selectedFolders={selectedFolders}
              setSelectedFolders={setSelectedFolders}
              foldersCount={foldersCount}
              showSelectAll={showSelectAll}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const FolderEntity = forwardRef(
  (
    {
      node,
      canShare,
      showCheckbox
    }: {
      node: AssetItemDto;
      canShare?: boolean;
      showCheckbox?: boolean;
    },
    ref: any
  ) => {
    const navigate = useNavigate();
    const assetsCountCopy = useMemo(() => {
      const count = node.folder?.totalAssetsCount || 0;
      return `${count} media`;
    }, [node.folder?.totalAssetsCount]);

    return (
      <div
        ref={ref}
        className="assets_folder_container"
        data-marquee-id={node.id}
        data-marquee-type="folder"
      >
        <div
          className="assets_folder"
          onClick={() => {
            if (node.deletedAt) return;
            navigate(`/campaigns/single/${node.campaignId}/${node.id}`);
          }}
          style={{
            cursor: node.deletedAt ? 'default' : undefined
          }}
        >
          <div className="assets_folder__body">
            {showCheckbox && (
              <Checkbox
                className="assets_folder__checkbox"
                value={node.id}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {node?.isPrivate ? (
              <FolderPrivateIcon className="assets_folder__icon" />
            ) : (
              <FolderIcon className="assets_folder__icon" />
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Tooltip
                placement="topLeft"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                title={node?.folder?.name}
              >
                <span>{node?.folder?.name}</span>
              </Tooltip>

              <span className="assets-count">{assetsCountCopy}</span>
            </div>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <FolderActions node={node} canShare={canShare} />
          </div>
        </div>
      </div>
    );
  }
);

export default FoldersList;
